'use client';

import { useSelector } from '@xstate/react';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

import { MfaRequestChannel, MfaVerifyChannel, WithdrawalBlockMfaScopes } from '@flipster/apis/v1/mfa/types';
import { zendesk } from '@flipster/features/src/shared/zendesk/url';
import { Button, TextButton } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';

import { mfaVerifyFlowBunja, mfaVerifyFormBunja } from '../bunja';
import { EmailOtpInput } from './EmailOtpInput';
import { SmsOtpInput } from './SmsOtpInput';
import { TotpOtpInput } from './TotpOtpInput';

interface Props {
  onClickConfirm?: (mfaSessionToken: string) => unknown;
}

const MfaForm = ({ onClickConfirm }: Props) => {
  const t = useTranslations();
  const { setError, resetField } = useBunja(mfaVerifyFormBunja);

  const [isLoading, setIsLoading] = useState(false);

  const { mfaVerifyActorAtom, destroy } = useBunja(mfaVerifyFlowBunja);
  const mfaVerifyActor = useAtomValue(mfaVerifyActorAtom);
  const { requireEmail, requireSms, requireTotp, maskedPhoneNumber } = useSelector(
    mfaVerifyActor,
    (snapshot) => snapshot.context,
  );
  const isWithdrawalBlockScope = useSelector(mfaVerifyActor, (snapshot) =>
    WithdrawalBlockMfaScopes.includes(snapshot.context.mfaScope),
  );

  const requestChannel = useCallback(
    (channel: MfaRequestChannel) => {
      resetField(channel);
      mfaVerifyActor.send({ type: `${channel}.request` });
    },
    [mfaVerifyActor, resetField],
  );

  const verifyChannel = (channel: MfaVerifyChannel, code: string) => {
    mfaVerifyActor.send({ type: `${channel}.verify`, code });
  };

  const submit = async () => {
    setIsLoading(true);
    const { mfaSessionToken } = mfaVerifyActor.getSnapshot().context;
    try {
      await onClickConfirm?.(mfaSessionToken);
    } finally {
      setIsLoading(false);
    }
  };

  const isChannelLoading = useSelector(mfaVerifyActor, (snapshot) => snapshot.context.isChannelLoading);
  const channelDetails = useSelector(mfaVerifyActor, (snapshot) => snapshot.context.channel);

  const canBeDone = useSelector(mfaVerifyActor, (snapshot) => snapshot.matches('WAIT_FOR_DONE'));

  useEffect(() => {
    const subscription = mfaVerifyActor.on('error', ({ channel, error }) => {
      const errorCode = getErrorCode(error);

      if (!errorCode) {
        resetField(channel);
        return;
      }

      setError(channel, getErrorMessage(error));
    });
    return () => subscription.unsubscribe();
  }, [mfaVerifyActor, requestChannel, resetField, setError, destroy]);

  return (
    <>
      <Modal.Content>
        {isWithdrawalBlockScope ? (
          <p className={hstack({ textStyle: 'Text/M/Regular', color: 'content.state.warning', gap: 4, mb: 16 })}>
            <CautionCircleIcon size={24} />
            <span className={css({ flex: 1 })}>{t('Mfa_verify.r250917.Withdrawal_24h_hold_notice')}</span>
          </p>
        ) : (
          <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-4', mb: 16 })}>
            {t('Mfa_verify.r250710.Identity_verification_description')}
          </p>
        )}
        <div className={stack({ gap: 16 })}>
          {requireTotp && (
            <TotpOtpInput
              loading={isChannelLoading}
              verified={channelDetails.totp?.verified}
              onSubmit={verifyChannel}
            />
          )}
          {requireSms && (
            <SmsOtpInput
              loading={isChannelLoading}
              expiresAt={channelDetails.sms?.expiresAt}
              verified={channelDetails.sms?.verified}
              disabled={!channelDetails.sms}
              phoneNumber={maskedPhoneNumber}
              onSubmit={verifyChannel}
              onSend={() => requestChannel('sms')}
            />
          )}
          {requireEmail && (
            <EmailOtpInput
              loading={isChannelLoading}
              expiresAt={channelDetails.email?.expiresAt}
              verified={channelDetails.email?.verified}
              disabled={!channelDetails.email}
              onSubmit={verifyChannel}
              onSend={() => requestChannel('email')}
            />
          )}
        </div>
        <TextButton
          css={{ alignSelf: 'flex-start' }}
          color='accent'
          size='medium'
          label={t('Mfa_verify.r250710.Contact_support_button')}
          onClick={() => {
            window.open(zendesk.getZendeskExternalUrl('RequestNew'), '_blank', 'noopener');
          }}
        />
      </Modal.Content>
      <Modal.Footer>
        <Button
          size='large'
          label={t('Common.r250401.Confirm')}
          isLoading={isLoading}
          disabled={!canBeDone}
          withSpinner
          onClick={submit}
        />
      </Modal.Footer>
    </>
  );
};

export default MfaForm;
